/**
 * ProjectGrid — top-level dashboard view when no project is selected.
 * Renders the synthetic Inbox card, all real projects, and a "+ New
 * project" tile at the end of the grid.
 */

import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { ProjectCard } from './ProjectCard';
import { SkeletonFlowCard } from './FlowCard';
import type { Project, InboxProjectId } from '../../types/project';
import { INBOX_PROJECT_ID } from '../../types/project';
import './ProjectCard.css';

export interface ProjectGridProps {
  projects: Project[];
  /**
   * True while the first fetch is still in flight. Without this the grid
   * rendered a complete-looking view — a "General · 0 Knods" tile and a "New
   * project" button — for the whole fetch, and the real projects popped in
   * afterwards. That read as the projects loading slowly even when they
   * didn't.
   */
  loading?: boolean;
  /**
   * Flow counts keyed by project id. Use INBOX_PROJECT_ID for the
   * Inbox count.
   */
  flowCountByProject: Record<string, number>;
  /**
   * Cover thumbnail URL keyed by project id (or INBOX_PROJECT_ID) —
   * typically the most recent flow's thumbnail.
   */
  coverByProject: Record<string, string | undefined>;
  onSelectProject: (id: string | InboxProjectId) => void;
  onCreateProject: () => void;
  onRenameProject: (project: Project) => void;
  onDeleteProject: (project: Project) => void;
  onArchiveProject: (project: Project) => void;
}

export const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  loading = false,
  flowCountByProject,
  coverByProject,
  onSelectProject,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onArchiveProject,
}) => {
  if (loading) {
    return (
      <div className="dashboard-flows-grid grid">
        {Array.from({ length: 8 }).map((_, index) => (
          <SkeletonFlowCard key={`project-skeleton-${index}`} viewMode="grid" index={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="dashboard-flows-grid grid">
      <ProjectCard
        project={null}
        flowCount={flowCountByProject[INBOX_PROJECT_ID] ?? 0}
        coverThumbnail={coverByProject[INBOX_PROJECT_ID]}
        onClick={() => onSelectProject(INBOX_PROJECT_ID)}
        animationIndex={0}
      />
      {projects.map((project, idx) => (
        <ProjectCard
          key={project.id}
          project={project}
          flowCount={flowCountByProject[project.id] ?? 0}
          coverThumbnail={coverByProject[project.id]}
          onClick={() => onSelectProject(project.id)}
          onRename={() => onRenameProject(project)}
          onDelete={() => onDeleteProject(project)}
          onArchive={() => onArchiveProject(project)}
          animationIndex={idx + 1}
        />
      ))}
      <button
        type="button"
        className="project-card-new"
        onClick={onCreateProject}
        style={{ animationDelay: `${(projects.length + 1) * 30}ms` }}
      >
        <AddIcon className="project-card-new-icon" />
        New project
      </button>
    </div>
  );
};

export default ProjectGrid;
